import * as monaco from 'monaco-editor';

export type TLineColState = { line: number; column: number; callStack: number };

export class BreakpointsContainer {
	public userBreakpoints: Set<number> = new Set<number>();
	public hoveredLine: number | null = null;
	public decorationsContainer: monaco.editor.IEditorDecorationsCollection;
	private decorations: monaco.editor.IModelDeltaDecoration[] = [];
	private awaitNextStepFlag: boolean = false;
	private lastStatePause: TLineColState | null = null;
	private terminationFlag: boolean = false;
	private stepOnNextType: 'any' | 'instruction' | 'breakpoint' = 'breakpoint';

	public constructor(private editor: monaco.editor.IStandaloneCodeEditor) {
		this.decorationsContainer = this.editor.createDecorationsCollection();
		this.prepareForNextExecution();
	}

	prepareForNextExecution() {
		this.awaitNextStepFlag = false;
		this.lastStatePause = null;
		this.terminationFlag = false;
		this.stepOnNextType = 'breakpoint';
	}

	private prepareDecorations(): void {
		const newDecorations = Array.from(this.userBreakpoints.keys()).map((lineNumber) => {
			return {
				range: new monaco.Range(lineNumber, 1, lineNumber, 1),
				options: {
					isWholeLine: true,
					glyphMarginClassName: 'my-debugger-breakpoint',
					glyphMarginHoverMessage: { value: 'Breakpoint' },
				},
			};
		});

		if (this.hoveredLine !== null) {
			const overClass = this.userBreakpoints.has(this.hoveredLine) ? '-over' : '';

			newDecorations.push({
				range: new monaco.Range(this.hoveredLine, 1, this.hoveredLine, 1),
				options: {
					isWholeLine: true,
					glyphMarginClassName: `my-debugger-breakpoint-hover${overClass}`,
					glyphMarginHoverMessage: { value: '' },
				},
			});
		}
		this.decorations = newDecorations;
	}

	terminateDebugger(): void {
		this.terminationFlag = true;
		this.awaitNextStepFlag = false;
	}

	isDebuggerTerminated(): boolean {
		return this.terminationFlag;
	}

	async awaitNextStep(lastStatePause: TLineColState): Promise<void> {
		this.lastStatePause = lastStatePause;
		this.awaitNextStepFlag = true;
		while (this.awaitNextStepFlag) {
			await new Promise((r) => setTimeout(r, 100));
		}
	}

	resolveNextStep(stepOnNextType: 'any' | 'instruction' | 'breakpoint'): void {
		this.awaitNextStepFlag = false;
		this.stepOnNextType = stepOnNextType;
	}

	shouldPauseHere(lineColState: TLineColState, args: { isInstruction: boolean; isEvaluated: boolean; isDebugPoint: boolean }) {
		// stop only on evaluated debug points, as it make no sense to stop it before
		if (args.isDebugPoint && args.isEvaluated) {
			return true;
		}

		switch (this.stepOnNextType) {
			case 'any': return true;
			// a bit more complex as this one skips function calls (calling a function should be a step "in" (ie, use any step type))
			case 'instruction': {
				if (args.isInstruction) {
					if (!this.lastStatePause || this.hasBreakpoint(lineColState.line)) return true;

					// we should skip if call comes from deeper call depths (those are instructions from deeper functions)
					// but we should pause if in SAME or SHALLOW depths (meaning the function has ended or instruction happened outside the function)
					return lineColState.callStack <= this.lastStatePause.callStack;
				}
				// we are not debugging "non instruction" on same line! (arg is not instruction)
				if (lineColState.line === this.lastStatePause?.line) return false;

				// if its a breakpoint on different line we need to pause here!
				return this.hasBreakpoint(lineColState.line);
			}
			// we stop on breakpoints when statement is not evaluated
			case 'breakpoint': return !args.isEvaluated && this.hasBreakpoint(lineColState.line);
		}
	}

	hasBreakpoint(lineNumber: number): boolean {
		return this.userBreakpoints.has(lineNumber);
	}

	render(): void {
		this.decorationsContainer.set(this.decorations);
	}

	toggleBreakpoint(lineNumber: number | undefined, render?: boolean): void {
		if (!lineNumber) return;

		if (this.userBreakpoints.has(lineNumber)) {
			this.userBreakpoints.delete(lineNumber);
		} else {
			this.userBreakpoints.add(lineNumber);
		}
		this.prepareDecorations();
		if (render) {
			this.render();
		}
	}

	onMouseMove(e: monaco.editor.IEditorMouseEvent, render?: boolean): void {
		const canPutBreakpoint = [
			monaco.editor.MouseTargetType.GUTTER_LINE_DECORATIONS,
			monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN,
		].includes(e.target.type);

		if (canPutBreakpoint) {
			const lineNumber = e.target.position?.lineNumber;

			if (!lineNumber) return;
			if (this.hoveredLine !== lineNumber) {
				this.hoveredLine = lineNumber;
				this.prepareDecorations();
			}
		} else {
			if (this.hoveredLine !== null) {
				this.hoveredLine = null;
				this.prepareDecorations();
			}
		}
		if (render) this.render();
	}
	onMouseDown(e: monaco.editor.IEditorMouseEvent, render?: boolean): void {
		const canPutBreakpoint = [
			monaco.editor.MouseTargetType.GUTTER_LINE_DECORATIONS,
			monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN,
		].includes(e.target.type);

		if (canPutBreakpoint) {
			const lineNumber = e.target.position?.lineNumber;

			this.toggleBreakpoint(lineNumber);
		}
		if (render) this.render();
	}
	onEditorChange(change: monaco.editor.IModelContentChange, render?: boolean): void {
		const startLine = change.range.startLineNumber;
		const startColumn = change.range.startColumn;
		const endLine = change.range.endLineNumber;
		const linesInserted = change.text.split('\n').length - 1;
		const linesDeleted = endLine - startLine;
		const delta = linesInserted - linesDeleted;

		if (delta === 0) {
			return;
		}

		const model = this.editor.getModel()!;
		const beforeText = model.getValueInRange({
			startLineNumber: startLine,
			startColumn: 1,
			endLineNumber: startLine,
			endColumn: startColumn,
		}).trim();

		const afterText = model.getValueInRange({
			startLineNumber: startLine,
			startColumn,
			endLineNumber: startLine,
			endColumn: model.getLineMaxColumn(startLine),
		}).trim();

		const newBps = Array.from(this.userBreakpoints.values()).map((bpLine) => {
			if (bpLine < startLine) {
				return bpLine;
			}
			if (startLine <= bpLine && bpLine <= endLine) {
				if (!beforeText.length && !afterText.length && delta < 0) return null;
				if (!beforeText.length) return bpLine + delta;
				if (!afterText.length) return bpLine;
				if (beforeText.length && afterText.length) {
					// if line with bp gets removed only remove breakpoint if
					// that line is not initial start line.
					if (delta < 0 && startLine < bpLine) return null;

					// else if the line is the initial start line, then keep the breakpoint
					return bpLine;
					// this together prevents removal of breakpoint if line with breakpoint is deleted and falls into
					// above line with breakpoint + that line without breakpoint inherits the breakpoint of the removed line's breakpoint
				}

				return bpLine;
			}

			return bpLine + delta;
		}).filter((v): v is number => v !== null);

		this.userBreakpoints = new Set(newBps);
		this.prepareDecorations();
		if (render) this.render();
	}
}
